import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, Input, cn } from '@cuspar/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { spefcificDomainZodRule } from './zod/specificDomain.zod';
import { useCartStore } from '../../state/cartStore';
import { PriceGadget } from '../UI/PriceGadget';
import { IPurchaseDomainType } from '@cuspar/ywt.ch.model';
import { getDomainPrice } from '../../pages/Product/RegisterDomainPage';

const formSchema = z.object({
  domain: spefcificDomainZodRule(['ch', 'li']),
  code: z.string().min(1),
});

export function DomainTransferForm({
  onSubmit,
  children,
  defaultDomain,
  className,
}: {
  onSubmit: (values: { domain: string; code: string }) => void;
  children?: ReactNode;
  defaultDomain?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const prices = useCartStore((state) => state.prices);

  // 1. Define a form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      domain: defaultDomain || '',
      code: '',
    },
  });

  // 2. Define a submit handler.
  function onFormSubmit(values: z.infer<typeof formSchema>) {
    onSubmit(values);
  }

  const search = form.watch('domain');

  const transferPrice = getDomainPrice({ domain: search, available: true, reason: '' }, prices, IPurchaseDomainType.Transfer);
  const renewalPrice = getDomainPrice({ domain: search, available: true, reason: '' }, prices, IPurchaseDomainType.Renewal);

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onFormSubmit)} className={cn('flex flex-col gap-4 justify-center items-center w-full', className)}>
          <FormField
            control={form.control}
            name='domain'
            render={({ field }) => (
              <FormItem className='w-full'>
                <FormLabel>{t('common.domain')}</FormLabel>
                <FormControl>
                  <Input autoCapitalize='off' autoCorrect='off' autoComplete='off' spellCheck={false} placeholder={t('domain.transferDomainPlaceholder')} {...field}></Input>
                </FormControl>
                <FormDescription>
                  {transferPrice && renewalPrice && (
                    <>
                      ".{transferPrice.tld}" transfers for <PriceGadget price={transferPrice.price} currency={transferPrice.currency} />, renews at{' '}
                      <PriceGadget price={renewalPrice.price} currency={renewalPrice.currency} /> / year
                    </>
                  )}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='code'
            render={({ field }) => (
              <FormItem className='w-full'>
                <FormLabel>{t('common.code')}</FormLabel>
                <FormControl>
                  <Input autoCapitalize='off' autoCorrect='off' autoComplete='off' spellCheck={false} placeholder={t('domain.authCodePlaceholder')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {children}
        </form>
      </Form>
    </>
  );
}
